/* ──────────────────────────────────────────────
 *  Panier unique du parcours de réservation (Parcours P2)
 *
 *  Un seul panier pour la fiche prestation du site, la barre de panier du site et le tunnel
 *  (page de réservation du site, /book, widget) : clé `cfixe_booking_cart_<slug>`, gardée
 *  7 jours après la dernière modification de la sélection. Il ne contient que des références
 *  (`BookingCartLine`) ; le tunnel les reconstruit contre le catalogue vivant
 *  (`rebuildBookingCartItems`), jamais un objet catalogue figé.
 *
 *  - `lines` : la sélection, signée de la visite (onglet) qui l'a écrite. La même visite la
 *    reprend directement ; une visite suivante la propose d'abord (bandeau « Reprendre /
 *    Recommencer » du tunnel), jamais en silence.
 *  - `incoming` : la prestation choisie sur une fiche du site, reprise par le tunnel à l'arrivée,
 *    pendant la visite qui l'a posée seulement.
 *  - `restorable` : la sélection d'une visite précédente que le tunnel a mise de côté le temps
 *    que le client réponde au bandeau.
 *  - `codes` : les codes du parcours (code promo, carte cadeau, codes d'un lien pas encore
 *    acceptés), gardés pendant la visite qui les a posés seulement : la barre de panier du site
 *    chiffre son total avec eux et le tunnel les reprend quand il rouvre.
 *
 *  Les anciennes clés (`cfixe_booking_cart` du site, `cfixe_booking_state_<slug>` du tunnel) sont
 *  lues tant que la nouvelle n'existe pas, et laissées en place jusqu'à stabilisation : un retour
 *  à la version précédente ne vide pas les paniers.
 * ────────────────────────────────────────────── */

import {
  BOOKING_CART_STORAGE_KEY,
  BOOKING_EVENT_CART_UPDATED,
  BOOKING_STATE_STORAGE_PREFIX,
  readSupplementQuantities,
  type SupplementQuantity,
} from '@cfixe-monorepo/shared';
import { sameCode } from '../utils/same-code';

export const BOOKING_CART_STORAGE_PREFIX = 'cfixe_booking_cart_';
/** Identifiant de la visite en cours (`sessionStorage` : un onglet, jusqu'à sa fermeture). */
export const BOOKING_VISIT_STORAGE_KEY = 'cfixe_booking_visit';
export const BOOKING_CART_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const BOOKING_CART_VERSION = 2;

/**
 * Une ligne du panier. `price_id` désigne la formule (identifiant stable) ; `duration_minutes` et
 * `price_index` ne servent qu'aux lignes reprises des anciens paniers, qui n'avaient pas d'identifiant.
 */
export interface BookingCartLine {
  prestation_id: number;
  price_id?: number;
  duration_minutes?: number;
  price_index?: number;
  quantity: number;
  supplement_ids: number[];
  /**
   * Quantités choisies au stepper pour des suppléments retenus qui se comptent au choix. Absentes : chaque
   * supplément suit les unités facturées de la ligne. Re-bornées contre le catalogue vivant à la reconstruction.
   */
  supplement_quantities?: SupplementQuantity[];
  /** Absent quand le pro n'ouvre pas la taille de groupe : jamais 1 par défaut. */
  party_size?: number;
}

export interface BookingCartIncoming {
  line: BookingCartLine;
  visit_id: string;
}

export interface BookingCartRestorable {
  lines: BookingCartLine[];
  updated_at: number;
}

/**
 * Codes du champ « Code promo ou carte cadeau » gardés pour la visite : le code promo et la carte cadeau posés
 * (acceptés par le serveur), et les codes apportés par un lien (`?code=`, `?promo=`) qu'il n'a pas encore
 * acceptés (`pending`, nature inconnue tant qu'il ne l'a pas tranchée) — le tunnel les réessaie à chaque
 * changement du panier.
 */
export interface BookingCartCodes {
  promo?: string;
  voucher?: string;
  pending?: string[];
}

export interface BookingCartVisitCodes extends BookingCartCodes {
  visit_id: string;
}

export interface BookingCart {
  v: typeof BOOKING_CART_VERSION;
  lines: BookingCartLine[];
  /** Dernière modification de la sélection : durée de vie et « il y a 2 jours » du bandeau. */
  updated_at: number;
  /** Visite qui a écrit la sélection en dernier (absente pour un panier repris d'une ancienne clé). */
  visit_id?: string;
  incoming?: BookingCartIncoming;
  restorable?: BookingCartRestorable;
  /** Codes du parcours, signés de la visite qui les a posés (jamais repris d'une visite précédente). */
  codes?: BookingCartVisitCodes;
}

export const bookingCartStorageKey = (professionalSlug: string): string =>
  `${BOOKING_CART_STORAGE_PREFIX}${professionalSlug}`;

const emptyCart = (now: number): BookingCart => ({ v: BOOKING_CART_VERSION, lines: [], updated_at: now });

const isBrowser = (): boolean => typeof window !== 'undefined';

// ── Visite ──────────────────────────────────────────

let memoryVisitId: string | undefined;

const randomVisitId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Identifiant de la visite en cours, créé à la première demande (mémoire seule si `sessionStorage` est fermé). */
export function getBookingVisitId(): string {
  try {
    const existing = sessionStorage.getItem(BOOKING_VISIT_STORAGE_KEY);
    if (existing) return existing;
    const created = randomVisitId();
    sessionStorage.setItem(BOOKING_VISIT_STORAGE_KEY, created);
    return created;
  } catch {
    memoryVisitId ??= randomVisitId();
    return memoryVisitId;
  }
}

/** Vrai si la sélection a été écrite pendant la visite en cours : le tunnel la reprend sans bandeau. */
export const isBookingCartFromThisVisit = (cart: BookingCart): boolean =>
  cart.lines.length > 0 && cart.visit_id !== undefined && cart.visit_id === getBookingVisitId();

// ── Lecture ─────────────────────────────────────────

const positiveInt = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;

const nonNegativeInt = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;

const idList = (value: unknown): number[] =>
  Array.isArray(value) ? value.filter((id): id is number => positiveInt(id) !== undefined) : [];

/** Quantités relues : celles des suppléments retenus seulement, une par supplément (le stockage peut être forgé). */
function selectedQuantities(raw: unknown, supplementIds: readonly number[]): SupplementQuantity[] {
  const selected = new Set(supplementIds);
  const seen = new Set<number>();
  return readSupplementQuantities(raw).flatMap((entry): SupplementQuantity[] => {
    const quantity = positiveInt(entry.quantity);
    if (quantity === undefined || !selected.has(entry.supplement_id) || seen.has(entry.supplement_id)) return [];
    seen.add(entry.supplement_id);
    return [{ supplement_id: entry.supplement_id, quantity }];
  });
}

/** Ligne relue du stockage : tout champ illisible est écarté (le stockage peut être ancien ou forgé). */
function parseLine(raw: unknown): BookingCartLine | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = raw as Record<string, unknown>;
  const prestationId = positiveInt(value.prestation_id);
  if (prestationId === undefined) return undefined;
  const priceId = positiveInt(value.price_id);
  const duration = positiveInt(value.duration_minutes);
  const priceIndex = nonNegativeInt(value.price_index);
  const partySize = positiveInt(value.party_size);
  const supplementIds = idList(value.supplement_ids);
  const quantities = selectedQuantities(value.supplement_quantities, supplementIds);
  return {
    prestation_id: prestationId,
    ...(priceId !== undefined ? { price_id: priceId } : {}),
    ...(duration !== undefined ? { duration_minutes: duration } : {}),
    ...(priceIndex !== undefined ? { price_index: priceIndex } : {}),
    quantity: positiveInt(value.quantity) ?? 1,
    supplement_ids: supplementIds,
    ...(quantities.length > 0 ? { supplement_quantities: quantities } : {}),
    ...(partySize !== undefined ? { party_size: partySize } : {}),
  };
}

const parseLines = (raw: unknown): BookingCartLine[] =>
  Array.isArray(raw) ? raw.map(parseLine).filter((line): line is BookingCartLine => line !== undefined) : [];

/** Même borne que `promo_code` / `voucher_code` / `code` de `CartSummaryRequestSchema`. */
const CODE_MAX_LENGTH = 64;

const codeString = (value: unknown): string | undefined => {
  const code = typeof value === 'string' ? value.trim() : '';
  return code.length > 0 && code.length <= CODE_MAX_LENGTH ? code : undefined;
};

/**
 * Codes nettoyés : chaînes vides et doublons écartés, un code en attente égal à un code posé oublié (il l'est
 * déjà). `undefined` quand il ne reste rien.
 */
function normalizeCodes(codes: BookingCartCodes): BookingCartCodes | undefined {
  const promo = codeString(codes.promo);
  const voucher = codeString(codes.voucher);
  const pending: string[] = [];
  for (const raw of codes.pending ?? []) {
    const code = codeString(raw);
    if (!code) continue;
    const known = [promo, voucher, ...pending].some((other): boolean => sameCode(other, code));
    if (!known) pending.push(code);
  }
  if (!promo && !voucher && pending.length === 0) return undefined;
  return {
    ...(promo ? { promo } : {}),
    ...(voucher ? { voucher } : {}),
    ...(pending.length > 0 ? { pending } : {}),
  };
}

/** Codes relus du stockage : illisibles ou sans visite, ils sont écartés. */
function parseCodes(raw: unknown): BookingCartVisitCodes | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = raw as Record<string, unknown>;
  if (typeof value.visit_id !== 'string' || value.visit_id.length === 0) return undefined;
  const codes = normalizeCodes({
    promo: codeString(value.promo),
    voucher: codeString(value.voucher),
    pending: Array.isArray(value.pending) ? value.pending.map(codeString).filter((c): c is string => !!c) : [],
  });
  return codes ? { ...codes, visit_id: value.visit_id } : undefined;
}

const isFresh = (updatedAt: number, now: number): boolean => now - updatedAt < BOOKING_CART_TTL_MS;

function parseCart(raw: string, now: number): BookingCart | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== 'object') return undefined;
  const value = parsed as Record<string, unknown>;
  if (value.v !== BOOKING_CART_VERSION) return undefined;
  const updatedAt = typeof value.updated_at === 'number' ? value.updated_at : 0;
  const cart: BookingCart = {
    ...emptyCart(updatedAt),
    // Sélection abandonnée trop longtemps : oubliée plutôt que ressortie des jours plus tard.
    lines: isFresh(updatedAt, now) ? parseLines(value.lines) : [],
  };
  if (typeof value.visit_id === 'string') cart.visit_id = value.visit_id;
  const incoming = value.incoming as Record<string, unknown> | undefined;
  const incomingLine = incoming ? parseLine(incoming.line) : undefined;
  if (incomingLine && typeof incoming?.visit_id === 'string') {
    cart.incoming = { line: incomingLine, visit_id: incoming.visit_id };
  }
  const restorable = value.restorable as Record<string, unknown> | undefined;
  const restorableAt = typeof restorable?.updated_at === 'number' ? restorable.updated_at : 0;
  const restorableLines = restorable && isFresh(restorableAt, now) ? parseLines(restorable.lines) : [];
  if (restorableLines.length > 0) cart.restorable = { lines: restorableLines, updated_at: restorableAt };
  const codes = parseCodes(value.codes);
  if (codes) cart.codes = codes;
  return cart;
}

/** Panier du tunnel d'avant P2 : `{ cartItems: [{ prestation_id, selected_price_index, … }], timestamp }`. */
function readLegacyTunnelLines(professionalSlug: string, now: number): { lines: BookingCartLine[]; at: number } {
  try {
    const raw = localStorage.getItem(`${BOOKING_STATE_STORAGE_PREFIX}${professionalSlug}`);
    if (!raw) return { lines: [], at: now };
    const state = JSON.parse(raw) as { cartItems?: unknown; timestamp?: unknown };
    const at = typeof state.timestamp === 'number' ? state.timestamp : 0;
    if (!isFresh(at, now) || !Array.isArray(state.cartItems)) return { lines: [], at: now };
    const lines = state.cartItems
      .map((item): BookingCartLine | undefined => {
        const ref = (item ?? {}) as Record<string, unknown>;
        return parseLine({
          prestation_id: ref.prestation_id,
          price_index: ref.selected_price_index,
          quantity: ref.quantity,
          supplement_ids: ref.selected_supplement_ids,
          supplement_quantities: ref.supplement_quantities,
          party_size: ref.party_size,
        });
      })
      .filter((line): line is BookingCartLine => line !== undefined);
    return { lines, at };
  } catch {
    return { lines: [], at: now };
  }
}

/** Panier du site d'avant P2 : `{ items: [{ prestation, selectedPrice, quantity, selectedSupplementIds, partySize }] }`. */
function readLegacySiteLines(): BookingCartLine[] {
  try {
    const raw = localStorage.getItem(BOOKING_CART_STORAGE_KEY);
    if (!raw) return [];
    const snapshot = JSON.parse(raw) as { items?: unknown };
    if (!Array.isArray(snapshot.items)) return [];
    return snapshot.items
      .map((item): BookingCartLine | undefined => {
        const value = (item ?? {}) as Record<string, unknown>;
        const prestation = (value.prestation ?? {}) as Record<string, unknown>;
        const price = (value.selectedPrice ?? {}) as Record<string, unknown>;
        return parseLine({
          prestation_id: prestation.prestation_id,
          price_id: price.price_id,
          duration_minutes: price.duration_minutes,
          quantity: value.quantity,
          supplement_ids: value.selectedSupplementIds,
          supplement_quantities: value.supplementQuantities,
          party_size: value.partySize,
        });
      })
      .filter((line): line is BookingCartLine => line !== undefined);
  } catch {
    return [];
  }
}

/**
 * Sélection reprise des anciennes clés, sans visite : le tunnel la proposera par le bandeau. Le panier
 * du tunnel passe d'abord ; une prestation du panier du site s'y ajoute si elle n'y est pas déjà.
 */
function readLegacyCart(professionalSlug: string, now: number): BookingCart {
  const tunnel = readLegacyTunnelLines(professionalSlug, now);
  const known = new Set(tunnel.lines.map((line): number => line.prestation_id));
  const site = readLegacySiteLines().filter((line): boolean => !known.has(line.prestation_id));
  const lines = [...tunnel.lines, ...site];
  return { ...emptyCart(tunnel.lines.length > 0 ? tunnel.at : now), lines };
}

/** Panier du pro (vide si rien n'est enregistré, illisible ou expiré). Lecture seule, sans effet. */
export function readBookingCart(professionalSlug: string, now: number = Date.now()): BookingCart {
  if (!isBrowser() || !professionalSlug) return emptyCart(now);
  let raw: string | null;
  try {
    raw = localStorage.getItem(bookingCartStorageKey(professionalSlug));
  } catch {
    return emptyCart(now);
  }
  if (raw === null) return readLegacyCart(professionalSlug, now);
  return parseCart(raw, now) ?? emptyCart(now);
}

// ── Écriture ────────────────────────────────────────

function writeBookingCart(professionalSlug: string, cart: BookingCart): void {
  if (!isBrowser() || !professionalSlug) return;
  try {
    localStorage.setItem(bookingCartStorageKey(professionalSlug), JSON.stringify(cart));
  } catch {
    // Stockage plein ou fermé : le panier vit alors le temps de la page.
  }
  window.dispatchEvent(new CustomEvent(BOOKING_EVENT_CART_UPDATED, { detail: { professionalSlug } }));
}

const updateBookingCart = (professionalSlug: string, update: (cart: BookingCart, now: number) => BookingCart): void => {
  const now = Date.now();
  writeBookingCart(professionalSlug, update(readBookingCart(professionalSlug, now), now));
};

/** Enregistre la sélection, signée de la visite en cours. */
export function saveBookingCartLines(professionalSlug: string, lines: BookingCartLine[]): void {
  updateBookingCart(
    professionalSlug,
    (cart, now): BookingCart => ({ ...cart, lines, updated_at: now, visit_id: getBookingVisitId() }),
  );
}

/** Prestation choisie sur une fiche du site : le tunnel la reprend à l'arrivée sur la page de réservation. */
export function setBookingCartIncoming(professionalSlug: string, line: BookingCartLine): void {
  updateBookingCart(
    professionalSlug,
    (cart): BookingCart => ({ ...cart, incoming: { line, visit_id: getBookingVisitId() } }),
  );
}

/** Prestation posée par une fiche pendant la visite en cours (celle d'une autre visite est ignorée). */
const incomingOfThisVisit = (cart: BookingCart): BookingCartLine | undefined =>
  cart.incoming && cart.incoming.visit_id === getBookingVisitId() ? cart.incoming.line : undefined;

/**
 * Prestation posée par une fiche, lue sans la retirer : le tunnel la garde en stockage tant que le catalogue n'a
 * pas chargé, pour qu'un rechargement après une panne la retrouve (`takeBookingCartIncoming` au premier succès).
 */
export function peekBookingCartIncoming(professionalSlug: string): BookingCartLine | undefined {
  return incomingOfThisVisit(readBookingCart(professionalSlug));
}

/** Retire la prestation posée par une fiche et la renvoie si elle vient de cette visite (sinon elle est oubliée). */
export function takeBookingCartIncoming(professionalSlug: string): BookingCartLine | undefined {
  const cart = readBookingCart(professionalSlug);
  if (!cart.incoming) return undefined;
  writeBookingCart(professionalSlug, { ...cart, incoming: undefined });
  return incomingOfThisVisit(cart);
}

/**
 * Met de côté la sélection d'une visite précédente (bandeau du tunnel) et la remplace par la sélection en
 * cours : un rechargement de page repose la même question au lieu de perdre l'une ou l'autre.
 */
export function setBookingCartRestorable(
  professionalSlug: string,
  restorable: BookingCartRestorable,
  lines: BookingCartLine[],
): void {
  updateBookingCart(
    professionalSlug,
    (cart, now): BookingCart => ({ ...cart, lines, updated_at: now, visit_id: getBookingVisitId(), restorable }),
  );
}

/** Réponse donnée au bandeau : la sélection mise de côté est oubliée (« Recommencer ») ou déjà reprise. */
export function clearBookingCartRestorable(professionalSlug: string): void {
  updateBookingCart(professionalSlug, (cart): BookingCart => ({ ...cart, restorable: undefined }));
}

/**
 * Enregistre les codes du parcours (posés, et en attente d'un panier qui les accepte), signés de la visite en
 * cours ; sans aucun code, ils sont oubliés. La sélection n'est pas touchée.
 */
export function saveBookingCartCodes(professionalSlug: string, codes: BookingCartCodes): void {
  const normalized = normalizeCodes(codes);
  updateBookingCart(
    professionalSlug,
    (cart): BookingCart => ({
      ...cart,
      codes: normalized ? { ...normalized, visit_id: getBookingVisitId() } : undefined,
    }),
  );
}

/** Codes gardés pendant la visite en cours (ceux d'une autre visite sont ignorés), sans leur signature. */
export function bookingCartCodes(cart: BookingCart): BookingCartCodes | undefined {
  if (!cart.codes || cart.codes.visit_id !== getBookingVisitId()) return undefined;
  const { promo, voucher, pending } = cart.codes;
  return normalizeCodes({ promo, voucher, pending });
}

/** Codes gardés pendant la visite en cours, lus dans le stockage (vide hors navigateur). */
export const readBookingCartCodes = (professionalSlug: string): BookingCartCodes | undefined =>
  bookingCartCodes(readBookingCart(professionalSlug));

/**
 * Vide le panier (réservation confirmée). La clé reste écrite, vide : les anciennes clés ne sont plus
 * relues et ne ressortent pas une sélection d'avant.
 */
export function clearBookingCart(professionalSlug: string): void {
  writeBookingCart(professionalSlug, emptyCart(Date.now()));
}

// ── Abonnement (useSyncExternalStore) ───────────────

/** Prévient à chaque écriture, dans cet onglet (événement du site) comme dans un autre (`storage`). */
export function subscribeBookingCart(listener: () => void): () => void {
  if (!isBrowser()) return (): void => undefined;
  const onStorage = (event: StorageEvent): void => {
    if (event.key === null || event.key.startsWith(BOOKING_CART_STORAGE_PREFIX)) listener();
  };
  window.addEventListener(BOOKING_EVENT_CART_UPDATED, listener);
  window.addEventListener('storage', onStorage);
  return (): void => {
    window.removeEventListener(BOOKING_EVENT_CART_UPDATED, listener);
    window.removeEventListener('storage', onStorage);
  };
}

const snapshots = new Map<string, { raw: string | null; cart: BookingCart }>();
const SERVER_SNAPSHOT: BookingCart = emptyCart(0);

/** Même objet tant que le stockage n'a pas changé (exigence de `useSyncExternalStore`). */
export function getBookingCartSnapshot(professionalSlug: string): BookingCart {
  if (!isBrowser() || !professionalSlug) return SERVER_SNAPSHOT;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(bookingCartStorageKey(professionalSlug));
  } catch {
    raw = null;
  }
  const cached = snapshots.get(professionalSlug);
  if (cached && cached.raw === raw) return cached.cart;
  const cart = readBookingCart(professionalSlug);
  snapshots.set(professionalSlug, { raw, cart });
  return cart;
}

export const getBookingCartServerSnapshot = (): BookingCart => SERVER_SNAPSHOT;
